import { PacketType } from './types'

const { VITE_ONYX_API_KEY, VITE_ONYX_API_URL } = import.meta.env

export type CreateSessionResponse = {
  chat_session_id: string
}

export type Source = {
  id: string
  semantic_identifier: string
  link: string
  blurb: string
  match_highlights: string[]
  source_type: string
  metadata: any[]
}

export type SendMessageResponse = {
  answer: string
  answer_citationless: string
  error_msg: string
  simple_search_docs: Source[]
}

function post(endpoint: string, data: any, signal?: AbortSignal) {
  return fetch(`${VITE_ONYX_API_URL}${endpoint}`, {
    method: 'POST',
    mode: 'cors',
    headers: {
      Authorization: `Bearer ${VITE_ONYX_API_KEY}`,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    },
    body: JSON.stringify(data),
    signal,
  })
}

export const createSession = async () => {
  const response = await post('/chat/create-chat-session', {
    persona_id: 0,
    description: null,
  })
  const data = (await response.json()) as CreateSessionResponse
  return data.chat_session_id
}

export async function* handleSSEStream<T extends PacketType>(
  streamingResponse: Response
): AsyncGenerator<T, void, unknown> {
  const reader = streamingResponse.body?.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const rawChunk = await reader?.read()
    if (!rawChunk) {
      throw new Error('Unable to process chunk')
    }
    const { done, value } = rawChunk
    if (done) {
      break
    }

    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() || ''

    for (const line of lines) {
      if (line.trim() === '') continue

      try {
        const data = JSON.parse(line) as T
        yield data
      } catch (error) {
        console.error('Error parsing SSE data:', error)

        // Detect JSON objects (ie. check if parseable json has been accumulated)
        const jsonObjects = line.match(/\{[^{}]*\}/g)
        if (jsonObjects) {
          for (const jsonObj of jsonObjects) {
            try {
              const data = JSON.parse(jsonObj) as T
              yield data
            } catch (innerError) {
              console.error('Error parsing extracted JSON:', innerError)
            }
          }
        }
      }
    }
  }

  // Process any remaining data in the buffer
  if (buffer.trim() !== '') {
    try {
      const data = JSON.parse(buffer) as T
      yield data
    } catch (error) {
      console.error('Error parsing remaining buffer:', error)
    }
  }
}

export async function* sendMessage({
  message,
  sessionId,
  parentMessageId,
  signal,
}: {
  message: string
  sessionId: string
  parentMessageId?: number
  signal?: AbortSignal
}) {
  const response = await post(
    '/chat/send-message',
    {
      alternate_assistant_id: 1, // Bitwarden.com assistant
      message,
      chat_session_id: sessionId,
      parent_message_id: parentMessageId || null,
      prompt_id: null,
      file_descriptors: [],
      // search_doc_ids: [],
      // retrieval_options: null,
      search_doc_ids: null,
      retrieval_options: {
        run_search: 'always',
        real_time: true,
        filters: {
          document_set: ['bitwarden.com'],
        },
      },
    },
    signal
  )

  yield* handleSSEStream<PacketType>(response)
}

export async function sendFeedback({
  chatMessageId,
  feedback,
  isPositive,
}: {
  chatMessageId: number
  isPositive: boolean
  feedback?: string
}) {
  return post('/chat/create-chat-message-feedback', {
    chat_message_id: chatMessageId,
    feedback_text: feedback,
    is_positive: isPositive,
  })
}
